import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { OrderDetail } from '@interfaces/OrderTypes';
import { SelfServiceMergePayload } from '@utils/orders/selfService';

const initialState: OrderDetail[] = [];

export const ordersWithDetailsSlice = createSlice({
  name: 'ordersWithDetails',
  initialState,
  reducers: {
    addOrderWithDetails: (state, action: PayloadAction<OrderDetail>) => {
      // Idempotente por id: si el pedido ya está en el store, se reemplaza EN SITIO (conserva
      // posición) en vez de prepender. Antes prependía sin deduplicar → un mismo id podía quedar
      // varias veces (p.ej. cada refetch del refresco de repartidor), y los consumidores por
      // `.find(pedido.id)` tomaban una entrada arbitraria. El sanado de auto-servicio ya NO pasa
      // por aquí (usa un fetch de solo-lectura), así que esto no pisa el estatus optimista.
      const exists = state.some((order) => order.pedido.id === action.payload.pedido.id);
      if (exists) {
        return state.map((order) => (order.pedido.id === action.payload.pedido.id ? action.payload : order));
      }
      return [action.payload, ...state]; // new orders are added at the beginning of the list
    },
    updateOrderWithDetails: (state, action: PayloadAction<OrderDetail>) => {
      return state.map((order) => {
        if (order.pedido.id === action.payload.pedido.id) {
          return action.payload;
        }
        return order;
      });
    },
    // Primitiva de merge segura (T2), espejo de `ordersSlice.mergeSelfService` sobre el detalle:
    // escribe SOLO `pedido.self_service` si el incoming trae `order_uuid` y el del store aún no
    // está enriquecido. Nunca toca `estatus`, `productos` ni ningún otro campo.
    mergeSelfService: (state, action: PayloadAction<SelfServiceMergePayload>) => {
      const { id, self_service } = action.payload;
      if (!self_service?.order_uuid) return state; // incoming sin enriquecer → no-op (anti des-agrupar)
      // El caso común del sanado T3 es un pedido que llegó `pending` y NUNCA entró a este slice
      // (getOrder solo cachea el detalle si estatus!=='pending' o ya venía enriquecido): sin match,
      // devolver un array nuevo forzaría un re-render no-op de todos los consumidores del detalle.
      // Solo cambia la referencia si algún elemento realmente se agrupó.
      let changed = false;
      const next = state.map((order) => {
        if (order.pedido.id !== id) return order;
        if (order.pedido.self_service?.order_uuid) return order; // ya agrupado → inmutable, no pisar
        changed = true;
        return { ...order, pedido: { ...order.pedido, self_service } };
      });
      return changed ? next : state;
    },
    reset: () => initialState,
  },
});
